import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../utils/safeToast';
import { Plus, Loader2, Search, Edit, Trash2, RotateCcw, Palette, User, Sparkles, Wrench } from 'lucide-react';

const API = "/api";

// Kategorien
const CATEGORIES = [
  { value: 'eigentuemer', label: 'Eigentümer', icon: User, color: '#3B82F6' },
  { value: 'reinigung', label: 'Reinigung', icon: Sparkles, color: '#10B981' },
  { value: 'handwerker', label: 'Handwerker', icon: Wrench, color: '#F59E0B' },
];

// Vordefinierte Farben
const PRESET_COLORS = [
  { name: 'Blau', value: '#3B82F6' },
  { name: 'Grün', value: '#10B981' },
  { name: 'Rot', value: '#EF4444' },
  { name: 'Gelb', value: '#F59E0B' },
  { name: 'Lila', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Grau', value: '#6B7280' },
];

const TagsPage = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, tag: null });
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
    category: 'eigentuemer',
    title_fragment: '',
    text_fragment: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTags();
  }, [showInactive]);

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/tags`, { params: { include_inactive: showInactive } });
      setTags(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Tags');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#3B82F6',
      category: 'eigentuemer',
      title_fragment: '',
      text_fragment: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Bitte einen Tag-Namen eingeben');
      return;
    }
    if (!formData.category) {
      toast.error('Bitte eine Kategorie auswählen');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tags`, {
        ...formData,
        title_fragment: formData.title_fragment || formData.name,
        text_fragment: formData.text_fragment || `Im Bereich ${formData.name} besteht Handlungsbedarf.`
      });
      toast.success('Tag erfolgreich erstellt');
      setDialogOpen(false);
      resetForm();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editDialog.tag) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/tags/${editDialog.tag.id}`, {
        name: formData.name,
        color: formData.color,
        category: formData.category,
        title_fragment: formData.title_fragment,
        text_fragment: formData.text_fragment
      });
      toast.success('Tag erfolgreich aktualisiert');
      setEditDialog({ open: false, tag: null });
      resetForm();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (tagId) => {
    if (!window.confirm('Tag wirklich deaktivieren?')) return;
    try {
      await axios.delete(`${API}/tags/${tagId}`);
      toast.success('Tag deaktiviert');
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Deaktivieren');
    }
  };

  const handleReactivate = async (tag) => {
    try {
      await axios.put(`${API}/tags/${tag.id}`, { is_active: true });
      toast.success('Tag reaktiviert');
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Reaktivieren');
    }
  };

  const openEditDialog = (tag) => {
    setFormData({
      name: tag.name,
      color: tag.color,
      category: tag.category || 'eigentuemer',
      title_fragment: tag.title_fragment || '',
      text_fragment: tag.text_fragment || ''
    });
    setEditDialog({ open: true, tag });
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[0];
  };

  const filteredTags = tags
    .filter(tag => tag.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(tag => categoryFilter === 'all' || tag.category === categoryFilter);

  const activeCount = tags.filter(t => t.is_active !== false).length;
  const inactiveCount = tags.filter(t => t.is_active === false).length;

  // Group tags by category for display
  const groupedTags = CATEGORIES.map(cat => ({
    ...cat,
    tags: filteredTags.filter(t => t.category === cat.value)
  }));
  const uncategorizedTags = filteredTags.filter(t => !t.category);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const TagForm = ({ onSubmit, isEdit = false }) => (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Tag-Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="z.B. Heizung, Fenster, Bad"
            required
            className="bg-stone-50"
            data-testid="tag-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Kategorie *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger className="bg-stone-50" data-testid="tag-category-select">
              <SelectValue placeholder="Kategorie wählen" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                    {cat.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title_fragment">Titel-Baustein</Label>
        <Input
          id="title_fragment"
          value={formData.title_fragment}
          onChange={(e) => setFormData({ ...formData, title_fragment: e.target.value })}
          placeholder={formData.name || "Wird im Aufgabentitel verwendet"}
          className="bg-stone-50"
          data-testid="tag-title-fragment"
        />
        <p className="text-xs text-stone-500">Wird für den automatischen Aufgabentitel verwendet</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="text_fragment">Text-Baustein</Label>
        <Textarea
          id="text_fragment"
          value={formData.text_fragment}
          onChange={(e) => setFormData({ ...formData, text_fragment: e.target.value })}
          placeholder={`Im Bereich ${formData.name || 'XXX'} besteht Handlungsbedarf.`}
          className="bg-stone-50 min-h-[80px]"
          data-testid="tag-text-fragment"
        />
        <p className="text-xs text-stone-500">Wird im generierten E-Mail-Text verwendet</p>
      </div>

      <div className="space-y-2">
        <Label>Farbe</Label>
        <div className="flex flex-wrap gap-2">
          {PRESET_COLORS.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => setFormData({ ...formData, color: color.value })}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                formData.color === color.value ? 'border-slate-900 scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: color.value }}
              title={color.name}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Palette className="w-4 h-4 text-stone-400" />
          <Input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            className="w-16 h-8 p-0 border-0 cursor-pointer"
          />
          <span className="text-sm text-stone-500">{formData.color}</span>
        </div>
      </div>

      <div className="pt-2 p-3 bg-stone-50 rounded-lg">
        <p className="text-sm text-stone-600 mb-2">Vorschau:</p>
        <div className="flex items-center gap-2">
          <Badge className="border-0 text-white" style={{ backgroundColor: formData.color }}>
            {formData.name || 'Tag-Name'}
          </Badge>
          <span className="text-sm text-stone-500">
            {getCategoryInfo(formData.category).label}
          </span>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (isEdit) {
              setEditDialog({ open: false, tag: null });
            } else {
              setDialogOpen(false);
            }
            resetForm();
          }}
          className="flex-1 rounded-full"
        >
          Abbrechen
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
          disabled={submitting}
          data-testid="tag-submit-btn"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {isEdit ? 'Speichern' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="tags-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Tag-Verwaltung
          </h1>
          <p className="text-stone-500 mt-1">{activeCount} aktive Tags (Textbausteine für Aufgaben)</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full"
          data-testid="add-tag-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Tag
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Tags suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-stone-200"
            data-testid="tag-search"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={categoryFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setCategoryFilter('all')}
            className={`rounded-full ${categoryFilter === 'all' ? 'bg-slate-700' : ''}`}
            size="sm"
          >
            Alle
          </Button>
          {CATEGORIES.map(cat => (
            <Button
              key={cat.value}
              variant={categoryFilter === cat.value ? 'default' : 'outline'}
              onClick={() => setCategoryFilter(cat.value)}
              className="rounded-full"
              size="sm"
              style={categoryFilter === cat.value ? { backgroundColor: cat.color } : {}}
            >
              <cat.icon className="w-4 h-4 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>
        {inactiveCount > 0 && (
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className={`rounded-full ${showInactive ? 'bg-stone-600' : ''}`}
            size="sm"
          >
            Inaktive ({inactiveCount})
          </Button>
        )}
      </div>

      {/* Tags Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-semibold text-slate-900">Tag</TableHead>
              <TableHead className="font-semibold text-slate-900">Kategorie</TableHead>
              <TableHead className="font-semibold text-slate-900">Titel-Baustein</TableHead>
              <TableHead className="font-semibold text-slate-900 max-w-[300px]">Text-Baustein</TableHead>
              <TableHead className="font-semibold text-slate-900 text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-stone-500">
                  Keine Tags gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredTags.map((tag) => {
                const catInfo = getCategoryInfo(tag.category);
                return (
                  <TableRow
                    key={tag.id}
                    className={`hover:bg-stone-50 ${tag.is_active === false ? 'opacity-50' : ''}`}
                    data-testid={`tag-row-${tag.id}`}
                  >
                    <TableCell>
                      <Badge
                        className="border-0 text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {tag.category ? (
                        <div className="flex items-center gap-2">
                          <catInfo.icon className="w-4 h-4" style={{ color: catInfo.color }} />
                          <span className="text-stone-600">{catInfo.label}</span>
                        </div>
                      ) : (
                        <span className="text-stone-400 italic">Keine</span>
                      )}
                    </TableCell>
                    <TableCell className="text-stone-700">
                      {tag.title_fragment || <span className="text-stone-400">–</span>}
                    </TableCell>
                    <TableCell className="text-stone-600 max-w-[300px] truncate" title={tag.text_fragment}>
                      {tag.text_fragment || <span className="text-stone-400">–</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {tag.is_active === false ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReactivate(tag)}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-600 hover:text-white"
                            data-testid={`reactivate-tag-${tag.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reaktivieren
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(tag)}
                              className="text-[#0E4C92] border-[#0E4C92] hover:bg-[#0E4C92] hover:text-white"
                              data-testid={`edit-tag-${tag.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Bearbeiten
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeactivate(tag.id)}
                              className="text-red-600 border-red-300 hover:bg-red-600 hover:text-white"
                              data-testid={`delete-tag-${tag.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Löschen
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Neuen Tag erstellen</DialogTitle>
          </DialogHeader>
          <TagForm onSubmit={handleCreate} />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => { if (!open) { resetForm(); setEditDialog({ open: false, tag: null }); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Tag bearbeiten</DialogTitle>
          </DialogHeader>
          <TagForm onSubmit={handleUpdate} isEdit />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TagsPage;
